// An intake question in the thread (spec §13.3; the intake skill's wording):
// the coach's question with its answers as chips, one tap each. The injury
// question's "yes" opens a short box for a few words. Answered, it shows the
// answer chosen; "a race" points at the calendar, where the race the coach
// waits for is added.
import React, { useState } from 'react';
import { Check, Flag } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button, Chip } from '../ds';
import { CardActions, CardTitle, CoachCard, Eyebrow } from './coachCard';
import { commands } from '../../v2/commands';
import { ANSWER_WITH_TEXT, RACE_ANSWER, questionView } from '../../v2/threadView';

/** `readOnly` (a coach viewing the athlete): the question and its chips, none of them answering. */
export default function QuestionCard({ item, pending = [], onAct, onAddRace, readOnly = false }) {
  const view = questionView(item, pending);
  const q = readOnly ? { ...view, open: false } : view;
  const [withText, setWithText] = useState(false);
  const [text, setText] = useState('');

  const answer = (id) => {
    if (!q.open) return;
    if (q.freeText && id === ANSWER_WITH_TEXT && !withText) { setWithText(true); return; }
    // "Yes" to the injury question needs its few words: an empty one tells the coach nothing (#69).
    if (q.freeText && id === ANSWER_WITH_TEXT && !text.trim()) return;
    onAct?.(commands.answerIntake(item, id, id === ANSWER_WITH_TEXT ? text : null));
  };

  return (
    <CoachCard tone={q.open ? 'accent' : 'plain'} role="group" aria-label={`Coach question: ${q.text}`}>
      <Eyebrow tone={q.open ? 'accent' : 'muted'}>Question</Eyebrow>
      <CardTitle>{q.text}</CardTitle>
      <div className="flex flex-wrap gap-1.5">
        {q.answers.map((a) => {
          const chosen = q.chosen === a.id;
          return (
            <Chip key={a.id} selected={chosen} disabled={!q.open} onClick={() => answer(a.id)}
              className={cn('gap-1', !q.open && !chosen && 'text-text-tertiary')}>
              {chosen && <Check size={12} />}{a.label}
            </Chip>
          );
        })}
      </div>
      {q.open && withText && (
        <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); answer(ANSWER_WITH_TEXT); }}>
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} autoFocus
            placeholder="A few words: what, where, since when"
            aria-label="Tell your coach about the injury"
            className="min-h-[40px] w-full rounded-[10px] border border-border-default bg-bg-canvas px-2.5 type-caption text-text-primary placeholder:text-text-tertiary focus:border-accent-default focus:outline-none" />
          <CardActions>
            <Button type="submit" size="small" disabled={!text.trim()}>Send</Button>
            <Button size="small" kind="text" onClick={() => { setWithText(false); setText(''); }}>Back</Button>
          </CardActions>
        </form>
      )}
      {q.sending && <p className="m-0 type-caption text-text-tertiary">Sending…</p>}
      {!readOnly && q.key === 'goal' && q.chosen === RACE_ANSWER && !q.open && (
        <div>
          <Button size="small" kind="secondary" icon={<Flag size={12} />} onClick={onAddRace}>Add your race</Button>
        </div>
      )}
    </CoachCard>
  );
}
